import React, { useState, useRef } from "react";
import { useRouter } from "next/router";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "@/components/ui/buttons/Button/Button";
import { site } from "config";
import styles from "./FormContainer.module.scss";
import Link from "next/link";

export default function FormContainer({
  children,
  pageName,
  submitHandler,
  onFormClose,
  formId,
  schema,
  submitButtonText = "Submit",
  confirmationText = "Thanks for your enquiry! We will be in contact shortly.",
}) {
  const {
    register,
    formState,
    getValues,
    handleSubmit,
    reset,
    trigger,
    clearErrors,
    setError,
    setValue,
    watch,
  } = useForm({
    resolver: !!schema ? yupResolver(schema) : null,
    mode: "onBlur",
    reValidateMode: "onBlur",
  });
  const [hasError, setHasError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const formChild = React.Children.only(children);
  const formComponent = React.cloneElement(formChild, {
    register,
    formState,
    isSubmitting,
    success,
    pageName,
    useFormMethods: {
      register,
      formState,
      getValues,
      handleSubmit,
      reset,
      trigger,
      clearErrors,
      setError,
      setValue,
    },
  });

  const router = useRouter();
  const formRef = useRef(null);

  const onSubmit = async () => {
    try {
      setIsSubmitting(true);
      setSuccess(false);
      setHasError(false);

      const { errors } = formState;

      if (Object.keys(errors)?.length) {
        console.error({
          message:
            "There was a problem submitting the form--please check the fields.",
          errors,
        });

        setSuccess(false);
        setIsSubmitting(false);

        return;
      }

      const pageData = {
        pageUri: `${site}${router.asPath}`,
        pageName: pageName,
      };

      const formValues = getValues();

      const response = await submitHandler({ formId, formValues, pageData });

      if (!!response && response?.status === "error") {
        throw new Error(JSON.stringify(response));
      }

      setIsSubmitting(false);
      setSuccess(true);

      reset();

      if (!!formRef) {
        formRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      return;
    } catch (error) {
      setHasError(true);
      setIsSubmitting(false);

      console.error(error.message);

      return;
    }
  };

  const handleFormClose = () => {
    setSuccess(false);

    if (!!onFormClose) {
      onFormClose();
    }
  };

  return (
    <FormProvider
      {...{
        register,
        formState,
        getValues,
        handleSubmit,
        reset,
        trigger,
        clearErrors,
        setError,
        setValue,
      }}
    >
      <div
        className={`${styles.formContainer} ${success ? styles.hide : ""}`}
        ref={formRef}
      >
        <form
          onSubmit={handleSubmit(async (data) => {
            await onSubmit(data);
          })}
        >
          {formComponent}
          {!!hasError && (
            <div className={`${styles.formError}`}>
              <p>
                There was an error while submitting your form. Please check the
                fields, try again, and <Link href="/contact">contact us</Link>{" "}
                directly if it continues to occur.
              </p>
            </div>
          )}
          <div className={`${styles.buttonContainer}`}>
            <Button
              htmlType="submit"
              disabled={isSubmitting || !!Object.keys(formState.errors).length}
              loading={isSubmitting}
              classes={["form-submit-button"]}
              // onClick={onSubmit}
            >
              {submitButtonText}
            </Button>
          </div>
        </form>
      </div>
      <dialog
        open={success}
        className={`${styles.successModal} ${success ? styles.active : ""}`}
      >
        <div className={`${styles.text}`}>
          <h2>Thank you!</h2>
          <p>{confirmationText}</p>
        </div>
        <div className={`${styles.buttonContainer}`}>
          <Button onClick={handleFormClose}>Close</Button>
        </div>
      </dialog>
    </FormProvider>
  );
}
